import { useContext, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { gql } from '@apollo/client';

import { HomeContext } from '../Home.context';
import {
	Issue,
	getAppToken,
	getUserRole,
	clone,
	getImagesFromIssue,
} from '../../../../constants';
import {
	GetIssuesResponse,
	GetIssuesArgs,
	sortIssuesReleaseDate,
} from '../../../../hooks/use-issue';
import { AppContext } from '../../../../App.context';
import config from '../../../../config';

export function useGetIssues(): GetIssuesResponse {
	const { appKey, apolloClient, onlineStatus, db, kv, sw } =
		useContext(AppContext);

	const [issues, setIssues] = useState<Array<Issue>>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);
	useEffect(() => {
		if (!apolloClient) {
			return;
		}
		if (onlineStatus === null) {
			return;
		}
		if (isLoading === false) {
			return;
		}
		if (!db) {
			return;
		}

		const props = {
			apolloClient,
			appKey,
			onlineStatus,
			sw,
			db,
			kv,
		};

		getIssues(props)
			.then((issues) => {
				setIssues(issues);
			})
			.catch((err) => {
				console.error(err);
				setError(err);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [apolloClient, onlineStatus, isLoading, sw, db]);
	return {
		issues,
		isLoading,
		error,
	};
}

export async function getIssues(args: GetIssuesArgs): Promise<Array<Issue>> {
	const { onlineStatus, db } = args;
	/*const issues = await getRemoteIssues(args);
	return issues.sort(sortReleaseDate);*/
	if (onlineStatus) {
		const issues = await getRemoteIssues(args);
		console.log(`ALL ISSUES`, issues);
		//kv.set('issues', clone(issues), null);
		// await db.saveIssues(issues);
		return issues.sort(sortIssuesReleaseDate);
	}

	return (await db.getIssues()).sort(sortIssuesReleaseDate);
}

async function getRemoteIssues(args: GetIssuesArgs) {
	const { apolloClient, appKey, sw, kv } = args;
	const fetchPolicyKey = 'remote-issues';

	const fetchPolicy = kv.get(fetchPolicyKey, 'no-cache');
	if (!kv.has(fetchPolicyKey)) {
		kv.set(
			fetchPolicyKey,
			'cache-first',
			config?.app?.cache?.ttl?.getAllIssues ?? undefined,
		);
	}

	const headers = {
		'app-key': appKey,
		Authorization: getAppToken(),
		'app-user-role': getUserRole(),
	};

	const result = await apolloClient.query({
		query: GET_ISSUES,
		fetchPolicy,
		headers,
	});

	const { issues } = clone(result.data);
	if (issues['__typename']) {
		delete issues['__typename'];
	}

	if (sw) {
		const images: Set<string> = new Set();
		getImagesFromIssue(images, issues);
		if (images.size) {
			sw.postMessage({
				type: 'DOWNLOAD_BACKGROUND_IMAGES',
				payload: {
					images: [...images],
				},
			});
		}
	}

	return issues;
}

export function useSelectIssue(issues: Array<Issue>) {
	const [searchParams, setSearchParams] = useSearchParams();
	const { setSelectedIssue } = useContext(HomeContext);
	useEffect(() => {
		if (!issues.length) {
			return;
		}
		const updatedSearchParams = new URLSearchParams(
			searchParams.toString(),
		);
		updatedSearchParams.set('section', 'magazines');

		const id = updatedSearchParams.get('id');
		if (!id) {
			setSearchParams(updatedSearchParams.toString());
			return;
		}

		const filteredIssue = issues
			.filter((issue) => `${issue.id}` === `${id}`)
			.pop();

		if (filteredIssue) {
			setSelectedIssue(filteredIssue);
			updatedSearchParams.set('id', filteredIssue.id);
			setSearchParams(updatedSearchParams.toString());
			return;
		}

		setSelectedIssue(null);
		updatedSearchParams.set('id', '');
		updatedSearchParams.delete('id');

		setSearchParams(updatedSearchParams.toString());
	}, [searchParams, issues]);
}

export const GET_ISSUES = gql`
	{
		issues {
			id
			name
			description
			cover
			externalUrl
			price
			index
			slug
			currency
			data
			updated
			created
			releaseDate
			isPurchased
			isPaid
		}
	}
`;
